import { createSlice } from "@reduxjs/toolkit";
import { set } from "nprogress";
import toast from "react-hot-toast";
import { v4 as uuidv4 } from "uuid";

const initialState = {
  id: null,
  splitCount: 2,
  baseTotal: 0,
  totalAmount: 0,
  receivedAmount: 0,
  remainingAmount: 0,
  payments: [],
};

const createInitialPayments = (n, total) => {
  const arr = Array.from({ length: n }, (_, i) => ({
    front_id: uuidv4(),
    amount: 0,
    name: `Payment ${i + 1}`, // Set default name here
    confirm: false,
    receivedAmount: 0,
    tipType: "fixed",
    tipValue: 0,
    tipAmount: 0,
    slots: [], // Default slot is now an empty array
  }));
  const base = Math.floor((total / n) * 100) / 100; // cents-safe base
  for (let i = 0; i < n; i++) arr[i].amount = base;
  // adjust last to cover rounding
  const sum = arr.reduce((s, p) => s + p.amount, 0);
  const diff = Math.round((total - sum) * 100) / 100;
  arr[n - 1].amount = Math.round((arr[n - 1].amount + diff) * 100) / 100;
  return arr;
};

const calculateTotals = (state) => {
  state.receivedAmount = state.payments.reduce(
    (total, payment) =>
      total + payment.slots.reduce((slotSum, slot) => slotSum + slot.amount, 0),
    0
  );
  const totalTips = state.payments.reduce((sum, payment) => sum + (Number(payment.tipAmount) || 0), 0);
  state.totalAmount = Math.round((state.baseTotal + totalTips) * 100) / 100;
  state.remainingAmount = Math.max(0, Math.round((state.totalAmount - state.receivedAmount) * 100) / 100);
};

const adjustOtherPayments = (payments, changedIndex, newAmount, baseTotal) => {
  // Iterate through all payments except the newly added one
  for (let i = 0; i < payments.length; i++) {
    if (i !== changedIndex) {
      // Calculate the total slot amount for the current payment
      const totalSlotAmount = payments[i].slots.reduce((sum, slot) => sum + slot.amount, 0);
      // Update the payment amount to match its total slot amount
      payments[i].amount = Math.round(totalSlotAmount * 100) / 100;
    }
  }

  // Ensure the total sum of all payments does not exceed the baseTotal
  const totalPaymentsAmount = payments.reduce((sum, payment) => sum + payment.amount, 0);
  const diff = Math.round((baseTotal - totalPaymentsAmount) * 100) / 100;

  if (Math.abs(diff) >= 0.01) {
    // Adjust the last payment to account for rounding differences
    payments[payments.length - 1].amount = Math.round((payments[payments.length - 1].amount + diff) * 100) / 100;
    if (payments[payments.length - 1].amount < 0) {
      payments[payments.length - 1].amount = 0;
    }
  }
};

const splitPaymentSlice = createSlice({
  name: "splitPayment",
  initialState: {
    ...initialState,
    payments: createInitialPayments(initialState.splitCount, initialState.remainingAmount ),
  },
  reducers: {
    setSplitCount: (state, action) => {
      state.splitCount = action.payload;
      state.payments = createInitialPayments(state.splitCount, state.baseTotal);
      calculateTotals(state);
    },
    newSplit: (state, action) => {
      const remainingAmount = state.remainingAmount;
      const newPayment = {
        front_id: uuidv4(),
        amount: remainingAmount,
        name: `Payment ${state.payments.length + 1}`,
        confirm: false,
        receivedAmount: 0,
        tipType: "fixed",
        tipValue: 0,
        tipAmount: 0,
        slots: [],
      };
      state.payments.push(newPayment);
      state.splitCount = state.payments.length;
      calculateTotals(state);
      // adjustOtherPayments(state.payments, state.payments.length - 1, remainingAmount, state.baseTotal);
    },
    updatePaymentAmount: (state, action) => {
      const { index, value } = action.payload;
      const newAmount = Number.isFinite(value) ? Math.round(value * 100) / 100 : 0;
      if (newAmount < 0) return;

      const updated = state.payments.map((p) => ({ ...p }));
      const oldAmount = Number(updated[index].amount) || 0;
      // old slots total for the changed payment
      const oldSlotsTotal = updated[index].slots.reduce((sum, s) => sum + s.amount, 0);
      if(newAmount < oldSlotsTotal) {
        toast.error("New amount cannot be less than the total of its slots.");
        return;
      }

      // compute how much others can give/take but never take below their slots totals
      const slotsTotals = updated.map((p) => p.slots.reduce((ss, s) => ss + s.amount, 0));
      const totalAvailableOthers = updated.reduce((s, p, i) => {
        if (i === index) return s;
        const avail = Math.max(0, Number(p.amount || 0) - (slotsTotals[i] || 0));
        return s + avail;
      }, 0);
      const maxIncrease = Math.round(totalAvailableOthers * 100) / 100 + oldAmount; // at most old + others' available

      // clamp requested amount so sum won't exceed totalAmount
      const clampedAmount = Math.min(newAmount, Math.round((state.baseTotal - 0) * 100) / 100);

      // If trying to increase beyond available in following splits, limit it
      const desired = Math.min(clampedAmount, maxIncrease);

      updated[index].amount = Math.round(desired * 100) / 100;

      // diff = how much we need to take from (if positive) or add to (if negative) following splits
      let diff = Math.round((desired - oldAmount) * 100) / 100; // positive means we must subtract from others

      if (diff > 0) {
        // subtract from subsequent splits in order (never below their slots totals)
        for (let i = index + 1; i < updated.length && diff > 0; i++) {
          const currAmount = Math.round(updated[i].amount * 100) / 100;
          const minAllowed = Math.round((slotsTotals[i] || 0) * 100) / 100;
          const avail = Math.max(0, Math.round((currAmount - minAllowed) * 100) / 100);
          const take = Math.min(avail, diff);
          updated[i].amount = Math.round((updated[i].amount - take) * 100) / 100;
          diff = Math.round((diff - take) * 100) / 100;
        }
        // if still diff > 0 try to take from previous splits (backwards)
        for (let i = index - 1; i >= 0 && diff > 0; i--) {
          const currAmount = Math.round(updated[i].amount * 100) / 100;
          const minAllowed = Math.round((slotsTotals[i] || 0) * 100) / 100;
          const avail = Math.max(0, Math.round((currAmount - minAllowed) * 100) / 100);
          const take = Math.min(avail, diff);
          updated[i].amount = Math.round((updated[i].amount - take) * 100) / 100;
          diff = Math.round((diff - take) * 100) / 100;
        }
        // if still diff remains, reduce the changed index accordingly but not below its slots total
        if (diff > 0) {
          const minForIndex = Math.round(oldSlotsTotal * 100) / 100;
          const currIndexAmount = Math.round(updated[index].amount * 100) / 100;
          const allowedReduce = Math.max(0, Math.round((currIndexAmount - minForIndex) * 100) / 100);
          const reduce = Math.min(allowedReduce, diff);
          updated[index].amount = Math.round((updated[index].amount - reduce) * 100) / 100;
          diff = Math.round((diff - reduce) * 100) / 100;
          if (diff > 0) {
            toast.error("Not enough available funds in other splits; amount adjusted to slots minimum.");
          }
        }
      } else if (diff < 0) {
        // negative diff: we freed money, distribute to subsequent splits in order
        let need = Math.abs(diff);
        for (let i = index + 1; i < updated.length && need > 0; i++) {
          updated[i].amount = Math.round((updated[i].amount + need) * 100) / 100;
          // we add whole remaining need into the next split (as user wanted sequential fill)
          need = 0;
        }
        // if still need > 0 (no later splits), add back to previous in reverse order
        for (let i = index - 1; i >= 0 && need > 0; i--) {
          updated[i].amount = Math.round((updated[i].amount + need) * 100) / 100;
          need = 0;
        }
      }

      // final safety: ensure no negative values and totals sum to totalAmount (fix rounding by adjusting last)
      for (let i = 0; i < updated.length; i++) if (updated[i].amount < 0) updated[i].amount = 0;

      const sum = updated.reduce((s, p) => s + Number(p.amount || 0), 0);
      const roundedSum = Math.round(sum * 100) / 100;
      const diffTotal = Math.round((state.baseTotal - roundedSum) * 100) / 100;
      if (Math.abs(diffTotal) >= 0.01) {
        // apply rounding fix to the last split but respect its slots minimum
        const lastIdx = updated.length - 1;
        const lastMin = Math.round((slotsTotals[lastIdx] || 0) * 100) / 100;
        let candidate = Math.round((updated[lastIdx].amount + diffTotal) * 100) / 100;
        if (candidate < lastMin) candidate = lastMin;
        updated[lastIdx].amount = candidate;
        if (updated[lastIdx].amount < 0) updated[lastIdx].amount = 0;
      }

      state.payments = updated;
      calculateTotals(state);
    },
    updatePaymentMethod: (state, action) => {
      const { index, method } = action.payload;
      state.payments[index].method = method;
    },
    updatePaymentName: (state, action) => {
      const { index, name } = action.payload;
      if (state.payments[index]) {
        state.payments[index].name = name;
      }
    },
    updatePaymentTip: (state, action) => {
      const { front_id, tipType, tipValue, tipAmount } = action.payload;
      const payment = state.payments.find((p) => p.front_id === front_id);
      if (payment) {
        payment.tipType = tipType;
        payment.tipValue = tipValue;
        payment.tipAmount = Math.max(0, Math.round((tipAmount || 0) * 100) / 100);
        calculateTotals(state);
      }
    },
    removePayment: (state, action) => {
      const { front_id } = action.payload;
      state.payments = state.payments.filter((payment) => payment.front_id !== front_id);
      state.splitCount = state.payments.length; // Update splitCount to reflect the new number of payments
    },
    addPaymentSlot: (state, action) => {
      const { front_id, slot } = action.payload;
      const payment = state.payments.find((payment) => payment.front_id === front_id);
      if (payment) {
        const totalSlotAmount = payment.slots.reduce((sum, s) => sum + s.amount, 0);
        const paymentDue = (Number(payment.amount) || 0) + (Number(payment.tipAmount) || 0);
        const remainingDue = Math.round((paymentDue - totalSlotAmount) * 100) / 100;
        const slotAmount = Math.round((Number(slot.amount) || 0) * 100) / 100;
        if (remainingDue >= slotAmount) {
          payment.slots.push(slot);
          calculateTotals(state);
        } else {
          toast.error("Slots amount exceeds the remaining payment amount (including tips).");
          console.error("Slots amount exceeds the remaining payment amount (including tips).");
        }
      } else {
        toast.error(`Payment with id ${front_id} not found.`);
        console.error(`Payment with id ${front_id} not found.`);
      }
    },
    resetSplit: (state, action) => {
      const { totalAmount = 0, order_id = null } = action?.payload || {};
      state.id = null;
      state.order_id = order_id;
      state.payment_id = null;
      state.restaurant_id = null;
      state.splitCount = 2;
      state.baseTotal = Number(totalAmount) || 0;
      state.totalAmount = Number(totalAmount) || 0;
      state.receivedAmount = 0;
      state.remainingAmount = Number(totalAmount) || 0;
      state.total_without_tips = Number(totalAmount) || 0;
      state.total_tips_amount = 0;
      state.payments = createInitialPayments(2, Number(totalAmount) || 0);
    },
    setPaymentAmount: (state, action) => {
      const { totalAmount = 0, remainingAmount = 0, order_id = null } = action.payload || {};
      state.id = null;
      state.order_id = order_id;
      state.payment_id = null;
      state.restaurant_id = null;
      state.splitCount = 2;
      state.baseTotal = Number(totalAmount) || 0;
      state.totalAmount = Number(totalAmount) || 0;
      state.remainingAmount = Number(remainingAmount ?? totalAmount) || 0;
      state.receivedAmount = 0;
      state.total_without_tips = Number(totalAmount) || 0;
      state.total_tips_amount = 0;
      state.payments = createInitialPayments(2, Number(totalAmount) || 0);
    },
    setPayments: (state, action) => {

      let paymentsWithSlots = action.payload?.map(payment => ({
        ...payment,
        slots: payment?.slots || [],
        tipType: payment?.tip_type || "fixed",
        tipValue: payment?.tip_value || 0,
        tipAmount: payment?.tip_amount || 0,
      })) || [];

      state.payments = paymentsWithSlots;
      // state.payments = action.payload;
      // calculateReceivedAndRemaining(state);
    },
    setSplitData: (state, action) => {
      const payload = action.payload || {};
      const serverPayments = payload.splitPayments || payload.payments || [];
      const baseTotal = payload.baseTotal ?? payload.total_without_tips ?? payload.total_amount ?? state.baseTotal;
      
      const paymentsWithSlots = serverPayments.map((payment, idx) => {
        const existing = Array.isArray(state.payments)
          ? state.payments.find(p => (payment.id && p.id === payment.id) || (payment.front_id && p.front_id === payment.front_id) || p.name === payment.name)
          : null;

        return {
          id: payment.id ?? existing?.id ?? null,
          restaurant_id: payment.restaurant_id ?? existing?.restaurant_id ?? null,
          order_id: payment.order_id ?? existing?.order_id ?? null,
          front_id: payment.front_id || payment.frontId || existing?.front_id || uuidv4(),
          name: payment.name || existing?.name || `Payment ${idx + 1}`,
          amount: Number(payment.amount) || 0,
          pay_amount: payment.pay_amount ?? existing?.pay_amount ?? null,
          change_amount: payment.change_amount ?? existing?.change_amount ?? null,
          advance_deducted: payment.advance_deducted ?? existing?.advance_deducted ?? null,
          payment_method: payment.payment_method ?? existing?.payment_method ?? null,
          payment_type: payment.payment_type ?? existing?.payment_type ?? null,
          status: payment.status ?? existing?.status ?? null,
          confirm: payment.confirm ?? existing?.confirm ?? false,
          receivedAmount: Number(payment.receivedAmount ?? payment.received_amount ?? 0),
          slots: Array.isArray(payment.slots) ? payment.slots.map(s => ({
            id: s.id ?? null,
            order_payment_split_payment_id: s.order_payment_split_payment_id ?? payment.id ?? null,
            method: (s.method || "card").toLowerCase(),
            type: s.type || (s.method === "cash" ? "manual" : "pax"),
            terminal_payment_id: s.terminal_payment_id ?? null,
            amount: Number(s.amount) || 0,
            received: Number(s.received ?? s.amount) || 0,
            change: Number(s.change ?? s.changed ?? 0) || 0,
            reference: s.reference ?? null,
          })) : [],
          tipType: payment.tip_type || payment.tipType || existing?.tipType || "fixed",
          tipValue: Number(payment.tip_value ?? payment.tipValue ?? existing?.tipValue) || 0,
          tipAmount: Number(payment.tip_amount ?? payment.tipAmount ?? existing?.tipAmount) || 0,
        };
      });

      state.id = payload.id ?? state.id;
      state.order_id = payload.order_id ?? state.order_id;
      state.payment_id = payload.payment_id ?? state.payment_id;
      state.restaurant_id = payload.restaurant_id ?? state.restaurant_id;
      state.splitCount = payload.split_count ?? payload.splitCount ?? (paymentsWithSlots.length || state.splitCount);
      state.baseTotal = Number(baseTotal) || 0;
      state.total_without_tips = Number(baseTotal) || 0;
      state.total_tips_amount = Number(payload.total_tips_amount ?? state.total_tips_amount ?? 0);
      state.payments = paymentsWithSlots;
      calculateTotals(state);

      if (payload.received_amount !== undefined && payload.received_amount !== null) {
        state.receivedAmount = Number(payload.received_amount);
      }
      if (payload.remaining_amount !== undefined && payload.remaining_amount !== null) {
        state.remainingAmount = Number(payload.remaining_amount);
      }
      if (payload.total_amount !== undefined && payload.total_amount !== null) {
        state.totalAmount = Number(payload.total_amount);
      }
    },
  },
});

export const { setSplitCount, updatePaymentAmount, updatePaymentMethod, updatePaymentName, updatePaymentTip, removePayment, addPaymentSlot, newSplit, setPaymentAmount, setPayments, setSplitData, resetSplit } = splitPaymentSlice.actions;
export default splitPaymentSlice.reducer;